/**
 * Project Detail → Timeline tab → BOARD view.
 *
 * Kanban-style board with one column per `TaskStatus`:
 *   Not started · Ongoing · Completed
 *
 * Same data source as the Calendar + List views (`useProjectTasks` →
 * the org-wide `tasks` collection). Drag-and-drop a card between
 * columns to change its `status` — fires a single `updateTask`
 * write that mobile picks up within ~1 second.
 *
 * Per card: title + category pill + dates + progress bar + assignee
 * + a "Log work" button (matching the List view's affordance).
 * Click anywhere else on the card → opens the edit form.
 *
 * Visual: reuses the existing `.kanban-board / .kanban-col /
 * .kanban-card` chrome from iio.css (introduced for the Leads
 * board), so the Timeline board feels like the Leads board.
 */
import { useMemo, useState } from 'react';

import { updateTaskStatus } from '@/lib/tasks';
import {
  taskStatusLabel,
  type TaskItem,
  type TaskStatus,
} from '@/lib/useProjectTasks';

// Column definition — order matters (left → right reflects the
// workflow Not started → Ongoing → Completed).
const COLUMNS: { status: TaskStatus; accent: string; sub: string }[] = [
  { status: 'not_started', accent: 'var(--text-tertiary)', sub: 'Backlog' },
  { status: 'ongoing', accent: 'var(--brand)', sub: 'In progress' },
  { status: 'completed', accent: 'var(--success)', sub: 'Done' },
];

const CATEGORY_LABEL: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  woodwork: 'Woodwork',
  design: 'Design',
  cleaning: 'Cleaning',
  floor_cleaning: 'Floor Cleaning',
  floor_matting: 'Floor Matting',
  painting: 'Painting',
  false_ceiling: 'False Ceiling',
  tiling: 'Tiling',
  general: 'General',
};

const CATEGORY_COLOR: Record<string, { bg: string; fg: string }> = {
  electrical: { bg: '#DBEAFE', fg: '#1D4ED8' },
  plumbing: { bg: '#D1FAE5', fg: '#065F46' },
  woodwork: { bg: '#FEF3C7', fg: '#92400E' },
  design: { bg: '#FCE7F3', fg: '#BE185D' },
  cleaning: { bg: '#CFFAFE', fg: '#0E7490' },
  floor_cleaning: { bg: '#CFFAFE', fg: '#0E7490' },
  floor_matting: { bg: '#ECFCCB', fg: '#3F6212' },
  painting: { bg: '#FFEDD5', fg: '#9A3412' },
  false_ceiling: { bg: '#FFEDD5', fg: '#9A3412' },
  tiling: { bg: '#FEE2E2', fg: '#B91C1C' },
  general: { bg: '#F3F4F6', fg: '#374151' },
};

function categoryLabel(key: string | undefined): string {
  if (!key) return 'General';
  return CATEGORY_LABEL[key] ?? key.replace(/_/g, ' ');
}

function categoryColor(key: string | undefined): { bg: string; fg: string } {
  return CATEGORY_COLOR[key ?? ''] ?? CATEGORY_COLOR.general;
}

function fmtShortDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
}

export function TimelineBoardView({
  tasks,
  onEditTask,
  onPostUpdate,
}: {
  tasks: TaskItem[];
  onEditTask: (task: TaskItem) => void;
  onPostUpdate?: (task: TaskItem) => void;
}) {
  /** Column currently being dragged-over (used for the highlight ring). */
  const [dragOver, setDragOver] = useState<TaskStatus | null>(null);
  /** Task id currently being dragged (used to dim its source card). */
  const [draggingId, setDraggingId] = useState<string | null>(null);
  /** Error from the move-write — surfaced as a small banner. */
  const [error, setError] = useState<string | null>(null);

  const buckets = useMemo(() => {
    const m = new Map<TaskStatus, TaskItem[]>();
    for (const col of COLUMNS) m.set(col.status, []);
    for (const t of tasks) {
      const arr = m.get(t.status as TaskStatus);
      if (arr) arr.push(t);
    }
    // Within each column: sort by startDate asc, undated last
    // (matches the List view's sort).
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const am = a.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
        const bm = b.startDate?.toMillis?.() ?? Number.POSITIVE_INFINITY;
        return am - bm;
      });
    }
    return m;
  }, [tasks]);

  const onDropOnColumn = async (status: TaskStatus, taskId: string) => {
    setDragOver(null);
    setDraggingId(null);
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === status) return;
    try {
      // updateTaskStatus is a one-field write — cheaper than the
      // full updateTask. Webhook-free, server has no triggers, so
      // mobile picks up the change within ~1s via the snapshot.
      await updateTaskStatus(taskId, status);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div>
      {error ? (
        <div
          className="card"
          style={{
            padding: 12,
            background: 'var(--critical-bg)',
            color: 'var(--critical-text)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 13 }}>Couldn't move task: {error}</span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="kanban-board">
        {COLUMNS.map((col) => {
          const rows = buckets.get(col.status) ?? [];
          const isOver = dragOver === col.status;
          return (
            <div
              key={col.status}
              className="kanban-col"
              onDragOver={(e) => {
                // preventDefault is REQUIRED for the drop event to
                // fire — HTML5 DnD default is "no drop allowed".
                e.preventDefault();
                if (dragOver !== col.status) setDragOver(col.status);
              }}
              onDragLeave={() => {
                if (dragOver === col.status) setDragOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                if (id) onDropOnColumn(col.status, id);
              }}
              style={{
                background: isOver ? 'var(--brand-bg)' : 'transparent',
                borderRadius: 8,
                padding: isOver ? '4px' : 0,
                transition: 'background 0.15s, padding 0.15s',
              }}
            >
              <div
                className="kanban-header"
                style={{
                  paddingLeft: 4,
                  paddingRight: 4,
                  borderLeft: `3px solid ${col.accent}`,
                  paddingTop: 4,
                  paddingBottom: 4,
                }}
              >
                <span className="kanban-title">
                  {taskStatusLabel(col.status)}
                </span>
                <span className="kanban-count">{rows.length}</span>
              </div>
              {rows.length === 0 ? (
                <div
                  style={{
                    border: '1px dashed var(--border)',
                    borderRadius: 8,
                    padding: 16,
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    textAlign: 'center',
                  }}
                >
                  Drop tasks here
                </div>
              ) : (
                rows.map((t) => (
                  <BoardCard
                    key={t.id}
                    task={t}
                    isDragging={draggingId === t.id}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', t.id);
                      e.dataTransfer.effectAllowed = 'move';
                      setDraggingId(t.id);
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setDragOver(null);
                    }}
                    onEdit={() => onEditTask(t)}
                    onPostUpdate={onPostUpdate}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───── Per-card ─────

function BoardCard({
  task,
  isDragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onPostUpdate,
}: {
  task: TaskItem;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onPostUpdate?: (task: TaskItem) => void;
}) {
  const cat = categoryColor(task.category);
  const sMs = task.startDate?.toMillis?.() ?? null;
  const eMs = task.endDate?.toMillis?.() ?? null;
  const pct = Math.max(0, Math.min(100, task.progress ?? 0));
  return (
    <div
      className="kanban-card"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onEdit}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.45 : 1,
        transform: isDragging ? 'rotate(1deg)' : undefined,
        transition: 'opacity 0.15s',
      }}
      title="Drag to another column to change status"
    >
      {/* Title + category */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 6,
          gap: 8,
        }}
      >
        <div
          className="k-card-title"
          style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
        >
          {task.title}
        </div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            padding: '3px 6px',
            borderRadius: 4,
            background: cat.bg,
            color: cat.fg,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {categoryLabel(task.category)}
        </span>
      </div>

      {/* Dates */}
      {sMs || eMs ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-secondary)',
            marginBottom: 8,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmtShortDate(sMs)} → {fmtShortDate(eMs)}
        </div>
      ) : null}

      {/* Progress bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div
          className="spent-bar"
          style={{ flex: 1, height: 5 }}
        >
          <div
            className="spent-bar-fill"
            style={{
              width: `${pct}%`,
              background:
                task.status === 'completed'
                  ? 'var(--success)'
                  : task.status === 'ongoing'
                    ? 'var(--brand)'
                    : 'var(--border)',
            }}
          />
        </div>
        <span
          style={{
            fontSize: 10,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--text-secondary)',
            fontWeight: 600,
            minWidth: 28,
            textAlign: 'right',
          }}
        >
          {pct}%
        </span>
      </div>

      {/* Footer: assignee + Log work */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          borderTop: '1px solid var(--border)',
          paddingTop: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {task.assignedToName ? (
            <>
              <i className="ph ph-user" /> {task.assignedToName}
            </>
          ) : (
            <span style={{ fontStyle: 'italic' }}>Unassigned</span>
          )}
        </div>
        {onPostUpdate ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onPostUpdate(task);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onPostUpdate(task);
              }
            }}
            // Don't let HTML5 drag start when clicking the chip.
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--brand)',
              padding: '3px 6px',
              border: '1px solid var(--brand)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
            title="Post a work log update"
          >
            <i className="ph-bold ph-plus" /> Log
          </span>
        ) : null}
      </div>
    </div>
  );
}
